import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const EXPORT_BATCH_SIZE = 100;   // records per single API request
const EXPORT_CHUNK_SIZE = 10000; // records per downloaded CSV file

const apiService = {
  getCompanies: async (page = 1, perPage = 10, industry = '', keywords = [], excludeKeywords = [], region = '', country = '', state = '') => {
    try {
      const response = await axios.post(`${API_BASE_URL}/companies`, {
        page,
        perPage,
        industry,
        keywords,
        excludeKeywords,
        region,
        country,
        state
      }, { timeout: 60000 });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch companies');
      }

      return response.data;

    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Network error';
      throw new Error(message);
    }
  },

  // Fetches ALL records and builds chunks in memory (no auto-download).
  // onProgress(fetched, total) — called after every batch
  // onChunkReady({ chunkNum, totalChunks, records }) — called as each 10k chunk fills up
  fetchAllForExport: async (industry, keywords, excludeKeywords = [], onProgress, onChunkReady, region = '', country = '', state = '') => {
    const APOLLO_PAGE_LIMIT = 500; // Apollo hard limit — page 501+ errors
    let buffer = [];
    let totalFetched = 0;
    let chunkNumber = 0;
    let page = 1;
    let totalRecords = 0;

    while (true) {
      const response = await axios.post(`${API_BASE_URL}/companies`, {
        page,
        perPage: EXPORT_BATCH_SIZE,
        industry,
        keywords,
        excludeKeywords,
        region,
        country,
        state
      }, { timeout: 60000 });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to fetch companies');
      }

      const batch = response.data.data;

      // Lock totalRecords to the first page's value.
      // Apollo's total_entries can drift on subsequent pages, which causes
      // totalFetched >= totalRecords to fire early and cut the export short.
      if (page === 1) totalRecords = response.data.pagination.totalRecords;

      // Primary stop: Apollo returned an empty page — no more records available
      if (batch.length === 0) break;

      buffer = buffer.concat(batch);
      totalFetched += batch.length;

      // Flush full 10k chunks as they fill up
      while (buffer.length >= EXPORT_CHUNK_SIZE) {
        chunkNumber++;
        const records = buffer.splice(0, EXPORT_CHUNK_SIZE);
        const totalChunks = Math.ceil(totalRecords / EXPORT_CHUNK_SIZE) || 1;
        if (onChunkReady) onChunkReady({ chunkNum: chunkNumber, totalChunks, records });
      }

      if (onProgress) onProgress(totalFetched, totalRecords);

      // Secondary stops: Apollo page cap or we have collected all expected records
      if (page >= APOLLO_PAGE_LIMIT || totalFetched >= totalRecords) break;
      page++;
    }

    // Flush any remaining records as the final partial chunk
    if (buffer.length > 0) {
      chunkNumber++;
      const totalChunks = Math.ceil(totalRecords / EXPORT_CHUNK_SIZE) || 1;
      if (onChunkReady) onChunkReady({ chunkNum: chunkNumber, totalChunks, records: buffer });
    }

    return { totalFetched, totalRecords, totalChunks: chunkNumber };
  },

  convertToCSV: (companies) => {
    if (!companies || companies.length === 0) return '';

    const headers = [
      'Company Name',
      'HQ Country',
      'HQ State',
      'HQ City',
      'Operating Geography',
      'Total Employees',
      'Year Founded',
      'Website',
      'Ownership Type',
      'Revenue ($)'
    ];

    const headerRow = headers.map(h => `"${h}"`).join(',');

    const dataRows = companies.map(company => {
      return headers.map(header => {
        let value = company[header] || '';
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""');
          if (value.includes(',') || value.includes('\n') || value.includes('"')) {
            value = `"${value}"`;
          }
        }
        return value;
      }).join(',');
    });

    return [headerRow, ...dataRows].join('\r\n');
  },

  downloadCSV: (csvContent, filename) => {
    const utf8Bom = '\uFEFF';
    const blob = new Blob([utf8Bom, csvContent], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
};

export default apiService;
